import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { GraduationCap, Clock, Globe, CheckCircle2, PlayCircle, ArrowLeft, Sparkles, Send, Award, Lock, ListChecks, FileText, Volume2, Download, ChevronDown } from "lucide-react";
import { PageHero } from "../components/FormKit";
import { Reviews } from "../components/Reviews";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { track } from "../lib/analytics";
import { toast } from "sonner";
import { INSTRUCTORS, LC_UI, LANGUAGES } from "../data/flagshipCourses";

const pKey = (slug) => `taven_progress_${slug}`;
const getP = (slug) => { try { return JSON.parse(localStorage.getItem(pKey(slug))) || {}; } catch { return {}; } };
const aiSid = (slug) => `tutor-${slug}-${localStorage.getItem("taven_ai_sid") || "guest"}`;

export default function RichCoursePage({ course }) {
  const { user, authApi } = useAuth();
  const [lang, setLang] = useState("English");
  const [tab, setTab] = useState("overview");
  const [progress, setProgress] = useState(() => getP(course.slug));
  const T = LC_UI[lang];
  const tr = lang === "English" ? null : course.i18n?.[{ "Français": "fr", "Kreyòl": "ht", "Español": "es" }[lang]];
  const instructor = INSTRUCTORS[course.instructor];

  const allLessons = course.modules.flatMap((m) => m.lessons.map((l) => l.title));
  const done = allLessons.filter((l) => progress[l]).length;
  const pct = Math.round((done / allLessons.length) * 100);

  useEffect(() => { track("course_view", { item_id: course.slug, item_name: course.title }); }, [course.slug]);
  useEffect(() => {
    if (!user) return;
    authApi.post("/customer/progress", { course_slug: course.slug, course_title: course.title, completed_lessons: done, total_lessons: allLessons.length }).catch(() => {});
  }, [progress, user]);

  const toggle = (l) => { const n = { ...progress, [l]: !progress[l] }; setProgress(n); localStorage.setItem(pKey(course.slug), JSON.stringify(n)); };

  const title = tr?.title || course.title;
  const summary = tr?.summary || course.summary;
  const objectives = tr?.objectives || course.objectives;

  const TABS = [
    { key: "overview", label: T.overview, icon: GraduationCap },
    { key: "lessons", label: T.lessons, icon: PlayCircle },
    { key: "coach", label: T.coach, icon: Sparkles },
    { key: "assessment", label: T.assessment, icon: ListChecks },
    { key: "certificate", label: T.certificate, icon: Award },
  ];

  return (
    <div data-testid="rich-course-page">
      <PageHero eyebrow={course.category} title={title} subtitle={summary}>
        <div className="flex flex-wrap gap-2 items-center">
          <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider bg-white/10 border border-white/20 px-3 py-1.5"><Clock className="w-3.5 h-3.5 text-taven-gold" /> {course.duration}</span>
          <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider bg-white/10 border border-white/20 px-3 py-1.5"><GraduationCap className="w-3.5 h-3.5 text-taven-gold" /> {course.level}</span>
          <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider bg-green-500/20 border border-green-400/40 text-green-200 px-3 py-1.5" data-testid="course-price"><Award className="w-3.5 h-3.5" /> Free · Certificate $10</span>
          <div className="flex gap-1 ml-1" data-testid="lang-switcher">
            {LANGUAGES.map((l) => (
              <button key={l} onClick={() => setLang(l)} data-testid={`lang-${l}`} className={`text-[11px] font-bold uppercase tracking-wider px-2.5 py-1.5 border ${lang === l ? "bg-taven-gold text-black border-taven-gold" : "border-white/25 text-white/80 hover:border-taven-gold"}`}>{l}</button>
            ))}
          </div>
        </div>
      </PageHero>

      <section className="py-8 px-4 md:px-8 lg:px-12 mx-auto max-w-6xl">
        <Link to="/education" className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-muted-foreground hover:text-black mb-6"><ArrowLeft className="w-3.5 h-3.5" /> Back to Academy</Link>

        <div className="bg-white border border-black/10 p-4 mb-6">
          <div className="flex items-center justify-between text-sm mb-2"><span className="font-bold">{T.yourProgress}</span><span className="text-taven-gold font-bold" data-testid="course-progress">{pct}% · {done}/{allLessons.length}</span></div>
          <div className="h-2 bg-taven-gray"><div className="h-2 bg-taven-orange transition-all" style={{ width: `${pct}%` }} /></div>
          <div className="flex flex-wrap gap-2 mt-4" data-testid="course-badges">
            {[
              { key: "started", label: "Course Started", earned: done > 0 },
              { key: "module", label: "Module Completed", earned: course.modules.some((m) => m.lessons.every((l) => progress[l.title])) },
              { key: "quiz", label: "Assessment Passed", earned: !!progress.__quizPassed },
              { key: "certified", label: "Certified", earned: !!progress.__certified },
            ].map((b) => (
              <span key={b.key} data-testid={`badge-${b.key}`} className={`flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 border ${b.earned ? "bg-taven-gold/15 text-taven-gold border-taven-gold/40" : "bg-taven-gray/40 text-black/30 border-black/5"}`}>
                <Award className="w-3 h-3" /> {b.label}
              </span>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap gap-2 border-b border-black/10 mb-6">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`} className={`flex items-center gap-1.5 px-4 py-3 text-xs font-bold uppercase tracking-wider border-b-2 -mb-px transition-colors ${tab === t.key ? "border-taven-orange text-black" : "border-transparent text-muted-foreground hover:text-black"}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {tab === "overview" && (
          <div className="grid lg:grid-cols-[1fr_320px] gap-8" data-testid="overview-tab">
            <div>
              <p className="text-sm text-muted-foreground leading-relaxed">{course.description}</p>
              <h3 className="text-xs font-bold uppercase tracking-[0.2em] mt-6 mb-3">{T.whatYouLearn}</h3>
              <ul className="grid sm:grid-cols-2 gap-2">{objectives.map((o) => <li key={o} className="flex items-start gap-2 text-sm"><CheckCircle2 className="w-4 h-4 text-taven-gold shrink-0 mt-0.5" /> {o}</li>)}</ul>
              {tr === null ? null : <p className="text-xs text-muted-foreground mt-4 italic">{T.pendingTranslation}</p>}
              <h3 className="text-xs font-bold uppercase tracking-[0.2em] mt-8 mb-3">{T.resources}</h3>
              <div className="space-y-2">
                {course.resources.map((r) => (
                  <div key={r.title} className="flex items-center justify-between border border-black/10 bg-white px-4 py-3" data-testid="resource-item">
                    <div className="flex items-center gap-2"><FileText className="w-4 h-4 text-taven-gold" /><div><p className="text-sm font-semibold">{r.title}</p><p className="text-xs text-muted-foreground">{r.note}</p></div></div>
                    <button onClick={() => toast.success("Resource will download once published by TAVEN")} className="text-xs font-bold uppercase tracking-wider text-taven-gold flex items-center gap-1"><Download className="w-3.5 h-3.5" /> {T.download}</button>
                  </div>
                ))}
              </div>
            </div>
            <aside className="lg:sticky lg:top-28 h-fit border border-black/10 bg-white p-6" data-testid="instructor-card">
              <p className="text-xs font-bold uppercase tracking-[0.2em] mb-4">{T.instructor}</p>
              <img src={instructor.avatar} alt={instructor.name} className="w-20 h-20 object-cover rounded-full border-2 border-taven-gold" />
              <p className="font-black font-display mt-3">{instructor.name}</p>
              <p className="text-xs text-taven-gold font-bold uppercase tracking-wider">{instructor.title}</p>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{instructor.bio}</p>
              <dl className="mt-4 space-y-2.5 border-t border-black/10 pt-4">
                {instructor.expertise && <div><dt className="text-[10px] font-bold uppercase tracking-wider text-black/50">Expertise</dt><dd className="text-sm">{instructor.expertise}</dd></div>}
                {instructor.teachingStyle && <div><dt className="text-[10px] font-bold uppercase tracking-wider text-black/50">Teaching Style</dt><dd className="text-sm">{instructor.teachingStyle}</dd></div>}
                {instructor.languages && <div><dt className="text-[10px] font-bold uppercase tracking-wider text-black/50">Languages</dt><dd className="text-sm">{instructor.languages}</dd></div>}
              </dl>
            </aside>
          </div>
        )}

        {tab === "lessons" && (
          <div className="space-y-5" data-testid="lessons-tab">
            {course.modules.map((m, mi) => {
              const av = INSTRUCTORS[m.avatar];
              return (
                <div key={m.title} className="border border-black/10 bg-white">
                  <div className="px-5 py-3 bg-taven-gray/50 font-bold flex items-center gap-2"><span className="text-taven-gold">{T.startModule} {mi + 1}</span> · {m.title}</div>
                  <div className="p-5 flex gap-4 items-start bg-taven-gold/5 border-b border-black/5" data-testid="avatar-guide">
                    <img src={av.avatar} alt={av.name} className="w-14 h-14 object-cover rounded-full border-2 border-taven-gold shrink-0" />
                    <div><p className="text-xs font-bold text-taven-gold uppercase tracking-wider">{av.name}</p><p className="text-sm text-muted-foreground italic mt-1">“{m.avatarLine}”</p></div>
                  </div>
                  <div className="px-5 py-4 flex items-center gap-3 border-b border-black/5">
                    <div className="w-full aspect-video max-w-md bg-taven-black flex flex-col items-center justify-center gap-1.5 relative" data-testid="lesson-video-placeholder">
                      <PlayCircle className="w-11 h-11 text-taven-gold/70" />
                      <p className="text-white/85 text-sm font-bold">Course video coming soon.</p>
                      <p className="text-white/40 text-xs">Real TAVEN Academy videos will appear here.</p>
                    </div>
                  </div>
                  <ul className="divide-y divide-black/5">
                    {m.lessons.map((les) => (
                      <LessonRow key={les.title} lesson={les} done={!!progress[les.title]} onToggle={() => toggle(les.title)} onAsk={() => setTab("coach")} T={T} />
                    ))}
                  </ul>
                  <div className="p-5 border-t border-black/10">
                    <ModuleQuiz quiz={m.quiz} label={`${T.quiz} · ${m.title}`} />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {tab === "coach" && <Tutor course={course} T={T} lang={lang} />}
        {tab === "assessment" && <FinalAssessment questions={course.finalAssessment} onPass={() => { track("course_complete", { item_id: course.slug }); const n = { ...progress, __quizPassed: true }; setProgress(n); localStorage.setItem(pKey(course.slug), JSON.stringify(n)); }} passed={progress.__quizPassed} onCert={() => setTab("certificate")} T={T} />}
        {tab === "certificate" && <CertificateBuy course={course} eligible={progress.__quizPassed} defaultName={user ? `${user.first_name} ${user.last_name}` : ""} defaultEmail={user?.email} />}

        <Reviews kind="course" target={course.slug} />
      </section>
    </div>
  );
}

const LessonRow = ({ lesson, done, onToggle, onAsk, T }) => {
  const [open, setOpen] = useState(false);
  const [showVo, setShowVo] = useState(false);
  return (
    <li>
      <div role="button" tabIndex={0} onClick={() => setOpen(!open)} onKeyDown={(e) => e.key === "Enter" && setOpen(!open)} className="w-full px-5 py-3 flex items-center gap-3 text-sm hover:bg-taven-gray/30 cursor-pointer">
        <button type="button" onClick={(e) => { e.stopPropagation(); onToggle(); }} className="shrink-0" data-testid={`lesson-complete-${lesson.title.slice(0,10)}`}>
          <CheckCircle2 className={`w-5 h-5 ${done ? "text-taven-orange fill-taven-orange/20" : "text-black/20"}`} />
        </button>
        <span className={`flex-1 ${done ? "line-through text-muted-foreground" : ""}`}>{lesson.title}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </div>
      {open && (
        <div className="px-5 pb-5 pt-1 bg-taven-gray/20 text-sm" data-testid="lesson-content">
          <p className="text-muted-foreground leading-relaxed">{lesson.content}</p>
          {lesson.examples?.length > 0 && (
            <div className="mt-3"><p className="text-xs font-bold uppercase tracking-wider mb-1">{T.examples}</p><ul className="list-disc pl-5 text-muted-foreground space-y-1">{lesson.examples.map((e) => <li key={e}>{e}</li>)}</ul></div>
          )}
          {lesson.exercise && <div className="mt-3 p-3 bg-white border border-black/10"><p className="text-xs font-bold uppercase tracking-wider text-taven-gold mb-1">{T.exercise}</p><p className="text-muted-foreground">{lesson.exercise}</p></div>}
          <div className="flex flex-wrap gap-3 mt-3">
            <button onClick={() => setShowVo(!showVo)} className="text-xs font-bold uppercase tracking-wider text-black flex items-center gap-1"><Volume2 className="w-3.5 h-3.5 text-taven-gold" /> {T.voiceover}</button>
            <button onClick={onAsk} className="text-xs font-bold uppercase tracking-wider text-taven-gold flex items-center gap-1"><Sparkles className="w-3.5 h-3.5" /> {T.askCoach}</button>
          </div>
          {showVo && <div className="mt-2 p-3 bg-taven-black text-white/90 text-sm italic" data-testid="voiceover-script">🎙 {lesson.voiceover}</div>}
        </div>
      )}
    </li>
  );
};

const ModuleQuiz = ({ quiz, label }) => {
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const score = quiz.filter((q, i) => answers[i] === q.answer).length;
  return (
    <div data-testid="module-quiz">
      <p className="text-xs font-bold uppercase tracking-[0.2em] mb-3">{label}</p>
      <div className="space-y-4">
        {quiz.map((q, i) => (
          <div key={i}>
            <p className="text-sm font-semibold mb-2">{i + 1}. {q.q}</p>
            <div className="grid sm:grid-cols-2 gap-2">
              {q.options.map((opt, oi) => (
                <label key={oi} className={`flex items-center gap-2 text-sm px-3 py-2 border cursor-pointer ${submitted && oi === q.answer ? "border-green-500 bg-green-50" : submitted && answers[i] === oi ? "border-red-400 bg-red-50" : "border-black/10 hover:border-taven-gold"}`}>
                  <input type="radio" name={`${label}-${i}`} checked={answers[i] === oi} onChange={() => setAnswers({ ...answers, [i]: oi })} /> {opt}
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>
      <button onClick={() => setSubmitted(true)} data-testid="module-quiz-submit" className="mt-4 bg-taven-black text-white font-bold uppercase tracking-wider text-xs py-2.5 px-6 hover:bg-taven-orange transition-colors">Check Answers</button>
      {submitted && <p className="mt-2 text-sm font-bold">Score: {score}/{quiz.length}</p>}
    </div>
  );
};

const FinalAssessment = ({ questions, onPass, passed, onCert, T }) => {
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const score = questions.filter((q, i) => answers[i] === q.answer).length;
  const pass = score >= Math.ceil(questions.length * 0.7);
  const submit = () => { setSubmitted(true); if (pass) { onPass(); toast.success(`Passed! ${score}/${questions.length}`); } else toast.error(`${score}/${questions.length}. Review and retry.`); };
  return (
    <div className="max-w-2xl" data-testid="assessment-tab">
      <h2 className="text-2xl font-black font-display mb-1">{T.assessment}</h2>
      <p className="text-sm text-muted-foreground mb-6">Score at least 70% to unlock your official certificate.</p>
      {passed && <div className="mb-6 p-4 bg-taven-gold/10 border border-taven-gold flex items-center justify-between"><span className="text-sm font-bold flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-taven-gold" /> Passed — certificate unlocked.</span><button onClick={onCert} className="text-xs font-bold uppercase tracking-wider text-taven-gold underline">Get Certificate →</button></div>}
      <div className="space-y-5">
        {questions.map((q, i) => (
          <div key={i} className="border border-black/10 bg-white p-5">
            <p className="font-semibold text-sm mb-3">{i + 1}. {q.q}</p>
            <div className="space-y-2">
              {q.options.map((opt, oi) => (
                <label key={oi} className={`flex items-center gap-2 text-sm px-3 py-2 border cursor-pointer ${submitted && oi === q.answer ? "border-green-500 bg-green-50" : submitted && answers[i] === oi ? "border-red-400 bg-red-50" : "border-black/10 hover:border-taven-gold"}`}>
                  <input type="radio" name={`fa${i}`} checked={answers[i] === oi} onChange={() => setAnswers({ ...answers, [i]: oi })} data-testid={`assessment-q${i}-opt${oi}`} /> {opt}
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>
      <button onClick={submit} data-testid="assessment-submit" className="mt-6 bg-taven-black text-white font-bold uppercase tracking-wider py-3.5 px-8 hover:bg-taven-orange transition-colors">Submit Final Assessment</button>
    </div>
  );
};

const Tutor = ({ course, T, lang }) => {
  const [messages, setMessages] = useState([{ role: "assistant", content: `Welcome! I'm your YEDA Coach for "${course.title}". Ask me anything about a lesson, request a summary, or practice with a quiz question. What would you like help with?` }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);
  const send = async () => {
    const msg = input.trim(); if (!msg || loading) return;
    setInput(""); setMessages((m) => [...m, { role: "user", content: msg }]); setLoading(true);
    try { const { data } = await api.post("/ai/chat", { session_id: aiSid(course.slug), message: msg, mode: "tutor", course: course.title, lesson: course.modules[0].title, language: lang || "English" }); setMessages((m) => [...m, { role: "assistant", content: data.reply }]); }
    catch { setMessages((m) => [...m, { role: "assistant", content: "The coach is resting a moment — please try again." }]); }
    finally { setLoading(false); }
  };
  return (
    <div className="border border-black/10 bg-white flex flex-col h-[60vh]" data-testid="coach-tab">
      <div className="bg-taven-black text-white p-4 flex items-center gap-3"><Sparkles className="w-5 h-5 text-taven-gold" /><div><p className="font-black font-display leading-none">YEDA Coach</p><p className="text-[10px] text-taven-gold uppercase tracking-widest mt-1">Ask only when you need help</p></div></div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.map((m, i) => <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}><div className={`max-w-[85%] px-3.5 py-2.5 text-sm ${m.role === "user" ? "bg-taven-black text-white" : "bg-taven-gray"}`}>{m.content}</div></div>)}
        {loading && <div className="text-sm text-muted-foreground">Coach is thinking…</div>}
        <div ref={endRef} />
      </div>
      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 border-t border-black/10 flex gap-2">
        <input data-testid="coach-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask the coach…" className="flex-1 bg-taven-gray px-3 py-2.5 text-sm outline-none" />
        <button data-testid="coach-send" disabled={loading} className="bg-taven-orange text-white p-2.5 hover:bg-[#d9561a] disabled:opacity-50"><Send className="w-4 h-4" /></button>
      </form>
    </div>
  );
};

const CertificateBuy = ({ course, eligible, defaultName, defaultEmail }) => {
  const [name, setName] = useState(defaultName || "");
  const [email, setEmail] = useState(defaultEmail || "");
  const [loading, setLoading] = useState(false);
  const buy = async () => {
    if (!name || !email) { toast.error("Enter your name and email."); return; }
    setLoading(true);
    try { const { data } = await api.post("/certificate/checkout", { origin_url: window.location.origin, course_slug: course.slug, course_title: course.title, student_name: name, student_email: email }); track("certificate_purchase", { item_name: course.title, value: 10, currency: "USD" }); window.location.href = data.url; }
    catch { toast.error("Unable to start checkout."); setLoading(false); }
  };
  return (
    <div className="max-w-2xl" data-testid="certificate-tab">
      <div className="border border-black/10 bg-white p-8 text-center">
        <Award className="w-14 h-14 text-taven-gold mx-auto" />
        <h2 className="text-2xl font-black font-display mt-4">Official TAVEN Certificate</h2>
        <p className="text-muted-foreground mt-2">The course is free. Unlock your official, verifiable certificate of completion for <strong>$10.00</strong>, issued by TAVEN SYSTEM LLC.</p>
        {!eligible ? (
          <div className="mt-6 p-4 bg-taven-gray/50 flex items-center justify-center gap-2 text-sm text-muted-foreground"><Lock className="w-4 h-4" /> Pass the Final Assessment to unlock your certificate.</div>
        ) : (
          <div className="mt-6 grid gap-3 text-left">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" data-testid="cert-name" className="border border-black/15 px-4 py-3 text-sm outline-none focus:border-taven-gold" />
            <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="Email" data-testid="cert-email" className="border border-black/15 px-4 py-3 text-sm outline-none focus:border-taven-gold" />
            <button onClick={buy} disabled={loading} data-testid="cert-buy" className="bg-taven-orange text-white font-bold uppercase tracking-wider py-4 hover:bg-[#d9561a] transition-colors disabled:opacity-60">{loading ? "Redirecting…" : "Get Official Certificate — $10"}</button>
          </div>
        )}
      </div>
    </div>
  );
};
